const MINUTES_PER_DAY = 24 * 60;

const timeToMinutes = (value) => {
  if (typeof value !== "string") return null;
  const match = /^(\d{1,2}):(\d{2})/.exec(value.trim());
  if (!match) return null;
  const hours = Number(match[1]);
  const minutes = Number(match[2]);
  if (!Number.isFinite(hours) || !Number.isFinite(minutes)) return null;
  return hours * 60 + minutes;
};

export const minutesToLabel = (minutes) => {
  const normalized = ((Math.round(minutes) % MINUTES_PER_DAY) + MINUTES_PER_DAY) % MINUTES_PER_DAY;
  const hours = Math.floor(normalized / 60);
  const mins = normalized % 60;
  return `${String(hours).padStart(2, "0")}:${String(mins).padStart(2, "0")}`;
};

// Ein Department/Tag-Besetzungsverlauf: jede Schicht mit Kopfzahl > 0 zaehlt als
// Intervall [start, end), das ihre Kopfzahl ueber diese Spanne beitraegt. Die
// Kurve ist die Summe aller aktiven Intervalle an jedem Zeitpunkt.
//
// Schichten, die ueber Mitternacht gehen (end <= start, z.B. 19:00-04:00),
// verlaengern das Intervall ueber 24:00 hinaus, statt es auf den Tagesanfang
// zurueckzuwickeln — ein Zurueckwickeln wuerde das Ende einer Nachtschicht auf
// dieselben Morgenstunden legen wie die Schichten des *naechsten* Tages und sie
// stillschweigend doppelt zaehlen oder falsch einordnen. Das bedeutet, die Achse
// kann fuer Abteilungen mit Nachtarbeit ueber 24:00 hinaus laufen; deshalb liefert
// buildOccupancyCurve rangeEnd zurueck, damit Aufrufer die Achse pro Tag statt mit
// einem festen 24h-Fenster dimensionieren.
export const buildOccupancyCurve = (shiftEntries) => {
  const intervals = (shiftEntries || [])
    .filter((entry) => entry && Number(entry.count) > 0 && entry.shift)
    .map(({ shift, count }) => {
      const start = timeToMinutes(shift.start_time);
      const rawEnd = timeToMinutes(shift.end_time);
      if (start == null || rawEnd == null || rawEnd === start) return null;
      const end = rawEnd < start ? rawEnd + MINUTES_PER_DAY : rawEnd;
      return { start, end, count: Number(count), shift };
    })
    .filter(Boolean);

  if (intervals.length === 0) {
    return {
      segments: [], intervals: [], breakpoints: [], min: 0, max: 0, rangeStart: 0, rangeEnd: 0,
    };
  }

  const breakpointSet = new Set();
  intervals.forEach(({ start, end }) => {
    breakpointSet.add(start);
    breakpointSet.add(end);
  });
  const breakpoints = Array.from(breakpointSet).sort((a, b) => a - b);

  const segments = [];
  for (let i = 0; i < breakpoints.length - 1; i += 1) {
    const start = breakpoints[i];
    const end = breakpoints[i + 1];
    const value = intervals.reduce((sum, interval) => (
      interval.start <= start && interval.end >= end ? sum + interval.count : sum
    ), 0);
    segments.push({ start, end, value });
  }

  const values = segments.map((segment) => segment.value);

  return {
    segments,
    intervals,
    breakpoints,
    min: Math.min(...values),
    max: Math.max(...values),
    rangeStart: breakpoints[0],
    rangeEnd: breakpoints[breakpoints.length - 1],
  };
};

// Gesamt-Kopfzahl eines Tages, unabhaengig davon *wann* — entscheidet zwischen
// "keine Schichten" (niemand eingeplant) und einer darstellbaren Kurve. Bewusst
// eine simple Summe von entry.count statt aus der Kurve abgeleitet, damit eine
// Abteilung mit definierten Schichten aber ueberall 0 Kopfzahl ebenfalls als
// "keine Schichten" gilt.
export const totalDayHeadcount = (shiftEntries) => (
  (shiftEntries || []).reduce((sum, entry) => sum + Number(entry?.count || 0), 0)
);

export const OCCUPANCY_STATUS = {
  CLOSED: "closed",
  EMPTY: "empty",
  POPULATED: "populated",
};

export const occupancyStatus = (isOpen, shiftEntries) => {
  if (isOpen === false) return OCCUPANCY_STATUS.CLOSED;
  if (totalDayHeadcount(shiftEntries) <= 0) return OCCUPANCY_STATUS.EMPTY;
  return OCCUPANCY_STATUS.POPULATED;
};

// "Nice" Y-Achsen-Schrittweite (1-2-5-Rundung) fuer die grosse Detailansicht,
// damit die Ticks wie 3/6/9/12 statt krummer Werte wie 2.75 herauskommen.
export const niceAxisStep = (roughStep) => {
  if (!Number.isFinite(roughStep) || roughStep <= 0) return 1;
  const magnitude = 10 ** Math.floor(Math.log10(roughStep));
  const normalized = roughStep / magnitude;
  let niceNormalized = 10;
  if (normalized <= 1) niceNormalized = 1;
  else if (normalized <= 2) niceNormalized = 2;
  else if (normalized <= 5) niceNormalized = 5;
  return Math.max(1, Math.round(niceNormalized * magnitude));
};

export const buildAxisTicks = (max, targetCount = 4) => {
  if (!Number.isFinite(max) || max <= 0) return [0];
  const step = niceAxisStep(max / targetCount);
  const ticks = [];
  for (let value = 0; value <= max + step - 1; value += step) {
    ticks.push(value);
  }
  return ticks;
};
